import React, { useState } from 'react';
import { Popover, Box, Typography } from '@mui/material';
import { IDocumentUnscopedExtraction, IDocumentUnscoped } from '../../types/my-types';
import CollapsedTitleWithTooltip from '../common/CollapsedTitleWithTooltip';
import { LinkTableStyled } from '../common/StyledComponents';

interface Props {
  dataPoolId: string;
  extractions: IDocumentUnscopedExtraction[];
  document: IDocumentUnscoped;
}

function ExtractionListCompact({ dataPoolId, extractions, document }: Props) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (!extractions || extractions.length === 0) {
    return <Typography variant="body2" color="textSecondary">—</Typography>;
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const verifiedCount = extractions.filter((e) => e.verified).length;

  return (
    <>
      <Box
        onClick={handleClick}
        data-cy={`badge-extraction-count-${document.id}`}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          height: '20px',
          px: '8px',
          fontSize: '11px',
          fontWeight: 500,
          borderRadius: '10px',
          border: '1px solid #1D9AAA',
          color: '#1D9AAA',
          backgroundColor: 'white',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          '&:hover': { backgroundColor: 'rgba(29,154,170,0.06)' },
        }}
      >
        {extractions.length} batch{extractions.length !== 1 ? 'es' : ''}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              mt: '4px',
            },
          },
        }}
      >
        <Box sx={{ p: '10px 12px', maxWidth: '280px', maxHeight: '300px', overflowY: 'auto' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '8px' }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#333' }}>
              Batches ({extractions.length})
            </Typography>
            {verifiedCount > 0 && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: '18px',
                  px: '6px',
                  fontSize: '10px',
                  fontWeight: 500,
                  borderRadius: '9px',
                  backgroundColor: '#66BBA9',
                  color: 'white',
                }}
              >
                {verifiedCount} verified
              </Box>
            )}
          </Box>

          {/* Batch list */}
          {extractions.map((extraction: IDocumentUnscopedExtraction, index: number) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: '5px',
                px: '6px',
                mx: '-6px',
                borderRadius: '4px',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' },
              }}
            >
              {extraction.extraction_batch ? (
                <>
                  <LinkTableStyled
                    to={`/data-pools/${dataPoolId}/extraction-batch/${extraction.extraction_batch}/details`}
                    data-cy={`link-extraction-batch-details-${extraction.extraction_batch_label}`}
                    onClick={handleClose}
                    style={{ fontSize: '13px' }}
                  >
                    <CollapsedTitleWithTooltip
                      title={extraction.extraction_batch_label || 'Deleted'}
                      charsFirstAndLast={18}
                    />
                  </LinkTableStyled>
                  {extraction.verified && (
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        height: '16px',
                        px: '5px',
                        ml: '8px',
                        fontSize: '10px',
                        fontWeight: 500,
                        borderRadius: '8px',
                        border: '1px solid #66BBA9',
                        color: '#66BBA9',
                        flexShrink: 0,
                      }}
                    >
                      Verified
                    </Box>
                  )}
                </>
              ) : (
                <Typography sx={{ fontSize: '13px', color: '#999' }}>
                  {extraction.extraction_batch_label || 'Deleted'}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
}

export default ExtractionListCompact;
